import React, { useEffect, useState } from 'react';
import { FaEye, FaEdit, FaBan, FaDownload, FaPlus, FaSort, FaSortUp, FaSortDown, FaCheck, FaTrash } from 'react-icons/fa';
import dayjs from 'dayjs';
import SubcategoryFormModal from './SubcategoryFormModal';
import SubcategoryViewModal from './SubcategoryViewModal';
import Sidebar from './Sidebar';
import { useNavigate } from 'react-router-dom';

const API_URL = '/api';

const SubcategoryMaster = () => {
  const [subcategories, setSubcategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [showView, setShowView] = useState(false);
  const [selectedSubcategory, setSelectedSubcategory] = useState(null);
  const [formLoading, setFormLoading] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  const [isScrolled, setIsScrolled] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    fetchSubcategories();
  }, []);

  // Add scroll listener for sticky header behavior
  useEffect(() => {
    const handleScroll = () => {
      const scrollTop = window.scrollY;
      setIsScrolled(scrollTop > 10);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const fetchSubcategories = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/subcategories`);
      if (!res.ok) {
        throw new Error(`HTTP error! status: ${res.status}`);
      }
      const data = await res.json();
      console.log('Subcategories API response:', data);
      // Ensure data is an array
      setSubcategories(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Error fetching subcategories:', error);
      setSubcategories([]);
    }
    setLoading(false);
  };

  const handleSort = (key) => {
    if (!Array.isArray(subcategories)) return;
    
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });

    const sortedSubcategories = [...subcategories].sort((a, b) => {
      if (a[key] === null) return 1;
      if (b[key] === null) return -1;
      
      // Handle date fields
      if (key === 'CreatedDate') {
        return direction === 'asc' 
          ? new Date(a[key]) - new Date(b[key])
          : new Date(b[key]) - new Date(a[key]);
      }
      
      // Handle numeric fields
      if (key === 'SubcategoryCode') {
        const aNum = parseInt(a[key].split('-')[1]);
        const bNum = parseInt(b[key].split('-')[1]);
        return direction === 'asc' ? aNum - bNum : bNum - aNum;
      }

      // Handle string fields
      if (a[key] < b[key]) return direction === 'asc' ? -1 : 1;
      if (a[key] > b[key]) return direction === 'asc' ? 1 : -1;
      return 0;
    });

    setSubcategories(sortedSubcategories);
  };

  const getSortIcon = (key) => {
    if (sortConfig.key !== key) return <FaSort className="ml-1" />;
    return sortConfig.direction === 'asc' 
      ? <FaSortUp className="ml-1" /> 
      : <FaSortDown className="ml-1" />;
  };

  const handleView = (subcategory) => {
    setSelectedSubcategory(subcategory);
    setShowView(true);
  };

  const handleEdit = (subcategory) => {
    setSelectedSubcategory(subcategory);
    setIsEdit(true);
    setShowForm(true);
  };

  const handleDelete = async (subcategory) => {
    if (!window.confirm('Are you sure you want to delete this subcategory?')) return;
    
    try {
      const response = await fetch(`${API_URL}/subcategories/${subcategory.SubcategoryCode}`, {
        method: 'DELETE',
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to delete subcategory');
      }
      
      fetchSubcategories();
    } catch (error) {
      console.error('Error deleting subcategory:', error);
      alert(error.message || 'An error occurred while deleting the subcategory');
    }
  };

  const handleDownloadCSV = () => {
    if (!Array.isArray(subcategories) || subcategories.length === 0) {
      alert('No subcategories to export');
      return;
    }
    
    const headers = [
      'Subcategory Code', 'Subcategory Name', 'Parent Category', 'Category Code', 'Description', 'Status', 'Created Date'
    ];
    const rows = subcategories.map(s => [
      s.SubcategoryCode, s.SubcategoryName, s.ParentCategoryName || '-', s.ParentCategoryCode || '-', 
      s.Description || '-', s.Status, dayjs(s.CreatedDate).format('YYYY-MM-DD HH:mm')
    ]);
    const csv = [headers, ...rows].map(r => r.join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'subcategories.csv';
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const handleCreate = () => {
    setSelectedSubcategory(null);
    setIsEdit(false);
    setShowForm(true);
  };

  const handleFormSubmit = async (formData) => {
    setFormLoading(true);
    try {
      if (isEdit && selectedSubcategory) {
        const response = await fetch(`${API_URL}/subcategories/${selectedSubcategory.SubcategoryCode}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(formData),
        });
        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.message || 'Failed to update subcategory');
        }
      } else {
        const response = await fetch(`${API_URL}/subcategories`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(formData),
        });
        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.message || 'Failed to create subcategory');
        }
      }
      setShowForm(false);
      fetchSubcategories();
    } catch (error) {
      console.error('Error saving subcategory:', error);
      alert(error.message || 'An error occurred while saving the subcategory');
    } finally {
      setFormLoading(false);
    }
  };

  const handleLogout = () => {
    navigate('/login');
  };

 return (
  <div className="min-h-screen flex bg-[#E5E1DA]">
    <Sidebar onLogout={handleLogout} />
    <main
      className="flex-1 p-2 md:p-8 overflow-hidden ml-0 md:ml-16 transition-all duration-300"
    >         <div className="flex flex-col h-full">
          <div className="flex flex-col sm:flex-row justify-center sm:justify-between items-center text-center sm:items-center mb-4 md:mb-6 gap-4">
            <h1 className="text-2xl md:text-3xl font-bold text-[#2b6777]">Subcategory Master</h1>
            <button
              className="flex items-center gap-2 bg-[#92C7CF] hover:bg-[#AAD7D9] text-white font-semibold px-4 py-2 rounded-lg shadow transition w-full sm:w-auto justify-center"
              onClick={handleCreate}
            >
              <FaPlus /> Create New Subcategory
            </button>
          </div>
          <div className="flex-1 overflow-hidden">
            <div className="h-full overflow-auto overflow-x-auto scroll-smooth">
              <table className="w-full bg-white rounded-2xl shadow-lg border-separate border-spacing-0 min-w-[900px]">
                <thead>
                  <tr>
                    <th 
                      className={`sticky top-0 z-20 bg-[#AAD7D9] w-auto text-[#2b6777] px-4 md:px-6 py-3 md:py-4 text-center border-r border-white text-xs md:text-sm font-bold rounded-tl-2xl cursor-pointer hover:bg-[#92C7CF] transition-all duration-300 ${isScrolled ? 'shadow-lg' : ''}`}
                      onClick={() => handleSort('SubcategoryCode')}
                    >
                      <div className="flex items-center justify-center">
                        Subcategory Code {getSortIcon('SubcategoryCode')}
                      </div>
                    </th>
                    <th 
                      className={`sticky top-0 z-20 bg-[#AAD7D9] text-[#2b6777] px-4 md:px-6 py-3 md:py-4 text-center border-r border-white text-xs md:text-sm font-bold cursor-pointer hover:bg-[#92C7CF] transition-all duration-300 ${isScrolled ? 'shadow-lg' : ''}`}
                      onClick={() => handleSort('SubcategoryName')}
                    >
                      <div className="flex items-center justify-center">
                        Subcategory Name {getSortIcon('SubcategoryName')}
                      </div>
                    </th>
                    <th 
                      className={`sticky top-0 z-20 bg-[#AAD7D9] text-[#2b6777] px-4 md:px-6 py-3 md:py-4 text-center border-r border-white text-xs md:text-sm font-bold cursor-pointer hover:bg-[#92C7CF] transition-all duration-300 ${isScrolled ? 'shadow-lg' : ''}`}
                      onClick={() => handleSort('ParentCategoryName')}
                    >
                      <div className="flex items-center justify-center">
                        Parent Category {getSortIcon('ParentCategoryName')}
                      </div>
                    </th>
                    <th 
                      className={`sticky top-0 z-20 bg-[#AAD7D9] text-[#2b6777] px-4 md:px-6 py-3 md:py-4 text-center border-r border-white text-xs md:text-sm font-bold cursor-pointer hover:bg-[#92C7CF] transition-all duration-300 ${isScrolled ? 'shadow-lg' : ''}`}
                      onClick={() => handleSort('ParentCategoryCode')}
                    >
                      <div className="flex items-center justify-center">
                        Category Code {getSortIcon('ParentCategoryCode')}
                      </div>
                    </th>
                    <th 
                      className={`sticky top-0 z-20 bg-[#AAD7D9] text-[#2b6777] px-4 md:px-6 py-3 md:py-4 text-center border-r border-white text-xs md:text-sm font-bold cursor-pointer hover:bg-[#92C7CF] transition-all duration-300 ${isScrolled ? 'shadow-lg' : ''}`}
                      onClick={() => handleSort('Description')}
                    >
                      <div className="flex items-center justify-center">
                        Description {getSortIcon('Description')}
                      </div>
                    </th>
                    <th 
                      className={`sticky top-0 z-20 bg-[#AAD7D9] text-[#2b6777] px-4 md:px-6 py-3 md:py-4 text-center border-r border-white text-xs md:text-sm font-bold cursor-pointer hover:bg-[#92C7CF] transition-all duration-300 ${isScrolled ? 'shadow-lg' : ''}`}
                      onClick={() => handleSort('Status')}
                    >
                      <div className="flex items-center justify-center">
                        Status {getSortIcon('Status')}
                      </div>
                    </th>
                    <th 
                      className={`sticky top-0 z-20 bg-[#AAD7D9] text-[#2b6777] px-4 md:px-6 py-3 md:py-4 text-center border-r border-white text-xs md:text-sm font-bold cursor-pointer hover:bg-[#92C7CF] transition-all duration-300 ${isScrolled ? 'shadow-lg' : ''}`}
                      onClick={() => handleSort('CreatedDate')}
                    >
                      <div className="flex items-center justify-center">
                        Created Date {getSortIcon('CreatedDate')}
                      </div>
                    </th>
                    <th className={`sticky top-0 z-20 bg-[#AAD7D9] text-[#2b6777] px-4 md:px-6 py-3 md:py-4 text-center text-xs md:text-sm font-bold rounded-tr-2xl transition-all duration-300 ${isScrolled ? 'shadow-lg' : ''}`}>
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr>
                      <td colSpan={8} className="text-center py-10 text-[#AAD7D9] font-semibold">
                        Loading subcategories...
                      </td>
                    </tr>
                  ) : subcategories && subcategories.length > 0 ? subcategories.map((subcategory, idx) => (
                    <tr
                      key={subcategory.SubcategoryCode}
                      className={`transition-all duration-300 group hover:bg-[#eaf6f6] hover:shadow-md border-b last:border-b-0 transform hover:scale-[1.01]`}
                      style={{ boxShadow: idx === subcategories.length - 1 ? '0 8px 24px 0 rgba(44,103,119,0.04)' : undefined }}
                    >
                      <td className="px-4 md:px-6 py-3 md:py-4 text-center text-xs md:text-sm font-mono whitespace-nowrap flex justify-center items-center">
                        <p className='cursor-pointer transition-all duration-200 hover:scale-105' style={{ color: 'blue', borderBottom: '1px solid blue', width: 'fit-content' }} onClick={() => handleView(subcategory)}>{subcategory.SubcategoryCode}</p>
                      </td>
                      <td className="px-4 md:px-6 py-3 md:py-4 text-center text-xs md:text-sm whitespace-nowrap font-semibold">{subcategory.SubcategoryName}</td>
                      <td className="px-4 md:px-6 py-3 md:py-4 text-center text-xs md:text-sm whitespace-nowrap">{subcategory.ParentCategoryName || '-'}</td>
                      <td className="px-4 md:px-6 py-3 md:py-4 text-center text-xs md:text-sm whitespace-nowrap">{subcategory.ParentCategoryCode || '-'}</td>
                      <td className="px-4 md:px-6 py-3 md:py-4 text-center text-xs md:text-sm break-all max-w-[200px]">{subcategory.Description || '-'}</td>
                      <td className="px-4 md:px-6 py-3 md:py-4 text-center text-xs md:text-sm">
                        <span className={`inline-block px-3 py-1 rounded-full text-xs font-bold shadow-sm transition-all duration-200
                          ${subcategory.Status === 'Active' ? 'bg-[#52ab98] text-white' : 'bg-gray-300 text-gray-500'}`}>
                          {subcategory.Status}
                        </span>
                      </td>
                      <td className="px-4 md:px-6 py-3 md:py-4 whitespace-nowrap text-xs md:text-sm text-gray-700">
                        {subcategory.CreatedDate ? (
                          <>
                            {dayjs(subcategory.CreatedDate).format('YYYY-MM-DD')}
                            <br />
                            <span className="text-xs text-gray-400">{dayjs(subcategory.CreatedDate).format('HH:mm')}</span>
                          </>
                        ) : '-'}
                      </td>
                      <td className="px-4 md:px-6 py-3 md:py-4 flex gap-2 justify-center items-center">
                        <button title="View" className="p-2 rounded-full hover:bg-[#c8d8e4] text-[#2b6777] transition-all duration-200 hover:scale-110" onClick={() => handleView(subcategory)}>
                          <FaEye />
                        </button>
                        <button title="Edit" className="p-2 rounded-full hover:bg-[#c8d8e4] text-[#2b6777] transition-all duration-200 hover:scale-110" onClick={() => handleEdit(subcategory)}>
                          <FaEdit />
                        </button>
                        <button title="Delete" className="p-2 rounded-full hover:bg-red-100 text-red-500 transition-all duration-200 hover:scale-110" onClick={() => handleDelete(subcategory)}>
                          <FaTrash />
                        </button>
                        <button title="Download CSV" className="p-2 rounded-full hover:bg-[#c8d8e4] text-[#2b6777] transition-all duration-200 hover:scale-110" onClick={handleDownloadCSV}>
                          <FaDownload />
                        </button>
                      </td>
                    </tr>
                  )) : (
                    <tr>
                      <td colSpan={8} className="text-center py-10 text-[#AAD7D9] font-semibold">
                        No subcategories found.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
          <SubcategoryFormModal
            open={showForm}
            onClose={() => setShowForm(false)}
            onSubmit={handleFormSubmit}
            initialData={selectedSubcategory}
            isEdit={isEdit}
            loading={formLoading}
          />
          <SubcategoryViewModal
            open={showView}
            onClose={() => setShowView(false)}
            subcategory={selectedSubcategory}
          />
        </div>
      </main>
    </div>
  );
};

export default SubcategoryMaster; 